import React from "react";
import { Card, CardContent, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { MoreHorizontal } from "lucide-react";
import { Badge } from "../ui/Badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

interface ActionItem {
  label: string;
  icon: React.ElementType;
  onClick: () => void;
  color?: "gray" | "red" | "blue" | "green" | "yellow" | "primary";
}

interface DataCardProps {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  iconBgColor?: string;
  status?: {
    label: string;
    color: "success" | "warning" | "info" | "gray" | "danger";
    icon?: React.ElementType;
  };
  badges?: Array<{
    label: string;
    color: "success" | "warning" | "info" | "gray" | "danger";
    icon?: React.ElementType;
  }>;
  stats?: Array<{
    label: string;
    value: string | number;
    color?: string;
  }>;
  tags?: string[];
  metadata?: Array<{
    label: string;
    value: string;
    icon?: React.ElementType;
  }>;
  actions?: ActionItem[];
  onCardClick?: () => void;
  className?: string;
}

const DataCard: React.FC<DataCardProps> = ({
  id,
  title,
  description,
  icon: Icon,
  iconBgColor = "from-blue-500 to-blue-600",
  status,
  badges = [],
  stats = [],
  tags = [],
  metadata = [],
  actions = [],
  onCardClick,
  className = "",
}) => {
  return (
    <Card
      className={`overflow-hidden hover:shadow-lg transition-all duration-200 border-gray-200 ${onCardClick ? "cursor-pointer" : ""} ${className}`}
      onClick={onCardClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${iconBgColor}`}
          >
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
              {title}
            </h3>
            <p className="text-sm text-gray-600 line-clamp-2">{description}</p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {/* Status and Badges */}
        {(status || badges.length > 0) && (
          <div className="flex items-center gap-2">
            {status && (
              <Badge variant={status.color} size="sm">
                <span className="inline-flex items-center gap-1">
                  {status.icon && <status.icon className="h-3.5 w-3.5" />}
                  <span>{status.label}</span>
                </span>
              </Badge>
            )}
            {badges.map((badge, index) => (
              <Badge key={index} variant={badge.color} size="sm">
                <span className="inline-flex items-center gap-1">
                  {badge.icon && <badge.icon className="h-3.5 w-3.5" />}
                  <span>{badge.label}</span>
                </span>
              </Badge>
            ))}
          </div>
        )}

        {/* Stats */}
        {stats.length > 0 && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            {stats.map((stat, index) => (
              <div key={index}>
                <p className="text-gray-500">{stat.label}</p>
                <p className={`font-semibold ${stat.color || "text-gray-900"}`}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="gray" size="sm">
                {tag}
              </Badge>
            ))}
            {tags.length > 3 && (
              <Badge variant="gray" size="sm">
                +{tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {/* Metadata */}
        {metadata.length > 0 && (
          <div className="space-y-1">
            {metadata.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-xs text-gray-500"
              >
                {item.icon && <item.icon className="h-3 w-3" />}
                <span>
                  {item.label}: {item.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Actions Dropdown Footer */}
        {actions.length > 0 && (
          <div className="flex items-center justify-end pt-3 border-t mt-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={e => e.stopPropagation()}
                >
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {actions.map((action, index) => {
                  const isDestructive = action.color === "red";
                  const showSeparator = isDestructive && index > 0;

                  return (
                    <React.Fragment key={index}>
                      {showSeparator && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        onClick={e => {
                          e.stopPropagation();
                          action.onClick();
                        }}
                        className={
                          isDestructive
                            ? "text-red-600 focus:text-red-600 focus:bg-red-50"
                            : ""
                        }
                      >
                        <action.icon className="h-4 w-4" />
                        <span>{action.label}</span>
                      </DropdownMenuItem>
                    </React.Fragment>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </Card>
  );
};

export default DataCard;
